// Resume Upload Step
import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, FileText, AlertCircle, CheckCircle } from 'lucide-react';
import { ResumeData } from '../../types';

interface ResumeUploadStepProps {
  resumeFile: File | null;
  resumeData: ResumeData | null;
  onResumeUpload: (file: File, extractedData: ResumeData) => void;
}

export const ResumeUploadStep: React.FC<ResumeUploadStepProps> = ({
  resumeFile,
  resumeData,
  onResumeUpload
}) => {
  const [error, setError] = useState<string | null>(null);

  const onDrop = useCallback((acceptedFiles: File[], fileRejections: any[]) => {
    // Handle file rejections
    if (fileRejections.length > 0) {
      const rejection = fileRejections[0];
      let errorMessage = 'File upload failed: ';
      
      if (rejection.errors.some((e: any) => e.code === 'file-too-large')) {
        errorMessage += 'File is too large. Maximum size is 10MB.';
      } else if (rejection.errors.some((e: any) => e.code === 'file-invalid-type')) {
        errorMessage += 'File type not supported. Please use PDF, doc, docx, txt files only.';
      } else {
        errorMessage += rejection.errors.map((e: any) => e.message).join(', ');
      }
      
      setError(errorMessage);
      return;
    }
    
    const file = acceptedFiles[0];
    if (file) {
      setError(null); // Clear any previous errors
      
      // Create a simple dummy ResumeData object
      const dummyResumeData: ResumeData = {
        personalInfo: {
          name: 'Resume Uploaded',
          email: 'user@example.com',
          phone: '',
          location: ''
        },
        experience: [],
        education: [],
        skills: [],
        certifications: [],
        projects: []
      };
      
      // Immediately call the callback with the file and dummy data
      onResumeUpload(file, dummyResumeData);
    }
  }, [onResumeUpload]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'text/plain': ['.txt'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
    },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024, // 10MB limit
  });

  if (resumeFile) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <div className="w-16 h-16 bg-success-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-success-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Resume Uploaded Successfully!
          </h2>
          <p className="text-gray-600">
            Your resume has been uploaded and will be processed by our AI during the interview setup.
          </p>
        </div>

        <div className="max-w-md mx-auto bg-gray-50 rounded-lg p-6">
          <div className="flex items-center">
            <FileText className="w-8 h-8 text-primary-600 mr-3" />
            <div>
              <p className="font-medium text-gray-900">{resumeFile.name}</p>
              <p className="text-sm text-gray-600">
                {(resumeFile.size / 1024 / 1024).toFixed(2)} MB
              </p>
            </div>
          </div>
        </div>

        <div className="text-center">
          <button
            onClick={() => {
              onResumeUpload(null as any, null as any);
            }}
            className="text-primary-600 hover:text-primary-700 text-sm font-medium"
          >
            Upload a different resume
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Upload className="w-8 h-8 text-primary-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Upload Your Resume
        </h2>
        <p className="text-gray-600">
          Upload your resume file. It will be processed by our AI to help personalize your interview experience.
        </p>
      </div>

      <div className="max-w-md mx-auto">
        <div
          {...getRootProps()}
          className={`
            border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
            ${isDragActive 
              ? 'border-primary-400 bg-primary-50' 
              : 'border-gray-300 hover:border-primary-400 hover:bg-primary-50'
            }
          `}
        >
          <input {...getInputProps()} />
          
          <div className="space-y-4">
            <FileText className="w-12 h-12 text-gray-400 mx-auto" />
            <div>
              <p className="text-gray-900 font-medium">
                {isDragActive ? 'Drop your resume here' : 'Click to upload or drag and drop'}
              </p>
              <p className="text-sm text-gray-600 mt-1">
                Supports PDF, doc, docx, txt files (Max 10MB)
              </p>
            </div>
          </div>
        </div>

        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
            <AlertCircle className="w-5 h-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-red-800 font-medium">Upload Error</p>
              <div className="text-red-700 text-sm mt-1">{error}</div>
            </div>
          </div>
        )}

        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start">
            <FileText className="w-5 h-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-blue-800 font-medium text-sm">Privacy Notice</p>
              <p className="text-blue-700 text-xs mt-1">
                Your resume will be processed securely by our AI to help personalize your interview experience.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
